import { BookOpen, Flame, Heart, Users } from "lucide-react";
import { PageHero, Reveal, SectionHeading } from "@/components/motion";
import { RecruitingForm } from "@/components/forms";

const STANDARDS = [
  {
    icon: Heart,
    title: "Great Character",
    text: "Teammates, coaches and families can all vouch for you. We recruit people before players — the talent is the bonus, the character is the price of entry.",
  },
  {
    icon: BookOpen,
    title: "Academic Scholars",
    text: "Scholarships are earned in classrooms and on transcripts as much as on fields and courts. We want athletes who attack school like they attack the weight room.",
  },
  {
    icon: Flame,
    title: "All-In Willingness",
    text: "The willingness to do whatever it takes — early mornings, extra film, hard practices — to earn the scholarship and finish the degree that changes a family's future.",
  },
];

const STEPS = [
  ["01", "Submit your profile", "Fill out the form on this page. That's your official entry into the process."],
  ["02", "Family evaluation call", "Our staff calls you and your parents — everyone hears the same plan at the same time."],
  ["03", "Visit the academy", "Walk the field, the court and the kitchen in Tuskegee. See where the year happens."],
  ["04", "Earn your spot", "If it's a fit, you're a Bulldog — and the real work starts immediately."],
  ["05", "Develop & get recruited", "A year of development, film and exposure aimed at one thing: your offer."],
];

export default function RecruitingPage() {
  return (
    <>
      <PageHero
        testid="recruiting-hero"
        eyebrow="Recruiting — class of 2027"
        lines={[<>Unsigned?</>, <span className="text-ice">Not for long.</span>]}
        sub="We are recruiting the best of the best unsigned players in the country — athletes with great character, real academic drive, and the willingness to do whatever it takes to achieve their dream of earning a scholarship and completing their degree to further their families."
      />

      {/* STANDARDS */}
      <section data-testid="prospect-standards" className="border-t border-white/5 py-24 sm:py-28">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <SectionHeading
            eyebrow="The standard"
            title={
              <>
                What a Bulldog <span className="text-ice">looks like.</span>
              </>
            }
          />
          <div className="mt-14 grid gap-6 md:grid-cols-3">
            {STANDARDS.map((s, i) => (
              <Reveal key={s.title} delay={i * 0.1} className="h-full">
                <div
                  data-testid="prospect-standards-card"
                  className="group h-full rounded-2xl border border-white/10 bg-navy-800 p-8 transition-colors hover:border-ice/50"
                >
                  <div className="grid h-12 w-12 place-items-center rounded-xl border border-ice/30 bg-ice/10 text-ice transition-colors group-hover:bg-ice group-hover:text-navy-900">
                    <s.icon className="h-6 w-6" />
                  </div>
                  <h3 className="mt-6 font-display text-2xl font-extrabold uppercase tracking-wide text-white">
                    {s.title}
                  </h3>
                  <p className="mt-3 text-sm leading-relaxed text-slate-300">{s.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* FAMILY JOURNEY */}
      <section data-testid="family-journey" className="border-t border-white/5 bg-navy-850 py-24 sm:py-28">
        <div className="mx-auto grid max-w-7xl gap-14 px-5 sm:px-8 lg:grid-cols-2">
          <div>
            <SectionHeading
              eyebrow="Families — this is for you too"
              title={
                <>
                  A journey <span className="text-crimson-bright">no one walks alone.</span>
                </>
              }
            />
            <Reveal delay={0.15}>
              <p className="mt-7 text-base leading-relaxed text-slate-300 sm:text-lg">
                Families need to know this upfront: the post-graduate journey is a process, and{" "}
                <span className="text-white">it takes everyone</span> — the athlete, the parents, the coaches and the
                staff pulling in the same direction.
              </p>
              <p className="mt-4 text-base leading-relaxed text-slate-300 sm:text-lg">
                Recruiting is very difficult for players to navigate on their own. Deadlines, eligibility rules,
                coach calls, visits, paperwork — no 18-year-old should carry that alone, and at ASA nobody does.
                Parents are part of every step: the calls, the visits and the decisions.
              </p>
            </Reveal>
          </div>

          <div className="relative">
            {STEPS.map(([n, t, d], i) => (
              <Reveal key={n} delay={0.1 + i * 0.08}>
                <div className="relative flex gap-6 pb-10 last:pb-0">
                  {i !== STEPS.length - 1 && (
                    <span className="absolute left-[22px] top-12 h-full w-px bg-gradient-to-b from-ice/40 to-transparent" />
                  )}
                  <span className="relative z-10 grid h-11 w-11 shrink-0 place-items-center rounded-full border border-ice/40 bg-navy-900 font-mono text-xs font-semibold text-ice">
                    {n}
                  </span>
                  <div>
                    <h3 className="font-display text-xl font-extrabold uppercase tracking-wide text-white">{t}</h3>
                    <p className="mt-1.5 text-sm leading-relaxed text-slate-300">{d}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ENTRY FORM */}
      <section id="recruit-form" data-testid="recruiting-form-section" className="border-t border-white/5 py-24 sm:py-28">
        <div className="mx-auto max-w-4xl px-5 sm:px-8">
          <SectionHeading
            align="center"
            eyebrow="Enter your info"
            title={
              <>
                Get in the <span className="text-ice">recruiting pipeline.</span>
              </>
            }
            description="Players: fill this out completely and honestly. Parents: your contact info is required — this process takes your whole family, and it starts with this form."
          />
          <Reveal delay={0.2} className="mt-12">
            <div className="glass rounded-2xl p-6 sm:p-8" data-testid="recruiting-entry-form-card">
              <RecruitingForm />
            </div>
          </Reveal>
          <Reveal delay={0.3}>
            <div className="mt-8 flex items-center justify-center gap-3 text-center">
              <Users className="h-4 w-4 shrink-0 text-ice" />
              <p className="font-mono text-[11px] uppercase tracking-[0.16em] text-slate-500">
                Every profile is reviewed by our recruiting staff — not a bot, not a portal.
              </p>
            </div>
          </Reveal>
        </div>
      </section>
    </>
  );
}
